import CateringNavItem from "~/modules/catering/components/CateringNavItem/CateringNavItem";
import { useCateringEntry } from "~/modules/catering/hooks/useCateringEntry";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

import styles from "./MenuBarSide.module.css";
import { useFetcher, useNavigate } from "@remix-run/react";
import LocationIcon from "~/icons/LocationIcon";
import UserIcon from "~/icons/UserIcon";
import {
  MenuBarSideListFontSize,
  MenuBarTopTitleFontSize,
} from "~/theme/models/themeModels";
import { useContext } from "react";
import AuthContext from "~/contexts/AuthContext";
import ThemeContext from "~/contexts/ThemeContext";
import { Menu, MenuShort } from "~/entities/menuEntities";
import { Site } from "~/entities/siteEntities";
import CircleCloseButton from "~/icons/CircleCloseIcon";
import { uploadcareConfig } from "~/configs/uploadcareConfig";
import ClockIcon from "~/icons/ClockIcon";
import {
  getApplicableStoreHours,
  mapSiteToSiteListObj,
  normaliseAvailability,
} from "~/helpers/siteHelpers";
import { combineDays, timeFromObject } from "~/helpers/dateHelper";
import { getSocialIcon } from "~/helpers/cssHelpers";
import OrderContext from "~/contexts/OrderContext";
import LogoutIcon from "~/icons/LogoutIcon";
import ChevronRight from "~/icons/ChevronRight";
import HistoryIcon from "~/icons/HistoryIcon";
import { urlGen } from "~/helpers/routesHelpers";

type MenuBarSideProps = {
  orgId: string;
  site?: Site;
  menus?: MenuShort[];
  menu?: Menu | null;
  orderId: string;
  visible: boolean;
  onClose: VoidFunction;
  goToMenu: (menuCode: string) => void;
  openAuthDialog: VoidFunction;
};

const MenuBarSide: React.FC<MenuBarSideProps> = ({
  site,
  menus,
  menu,
  orderId,
  visible,
  onClose,
  goToMenu,
  openAuthDialog,
  orgId,
}) => {
  const fetcher = useFetcher();
  const navigate = useNavigate();

  const { authenticatedUser, isAuthEnabled, useComoAuth, openMyAccountModal } =
    useContext(AuthContext);
  const { template } = useContext(ThemeContext);
  const { orderSummary } = useContext(OrderContext);
  const cateringEntry = useCateringEntry(site, visible, onClose);

  const backgroundColor = template.menuBar.general.backgroundColour;
  const secondaryBackgroundColor =
    template.menuBar.general.secondaryBackgroundColour;
  const menuItemFontSize =
    MenuBarSideListFontSize[template.menuBar.menuItem.fontSize];
  const menuItemTitleFontSize =
    MenuBarTopTitleFontSize[template.menuBar.menuItem.fontSize];
  const menuItemFontColor = template.menuBar.menuItem.fontColour;
  const showIcons = template.menuBar.menuItem.showIcons;
  const iconColor = template.menuBar.menuItem.iconColour;

  return (
    <>
      <div
        className={clsx(
          styles.drawerBackdrop,
          visible && styles.drawerBackdropVisible,
        )}
        onClick={() => visible && onClose()}
      />
      <div
        className={clsx(styles.leftDrawer, visible && styles.leftDrawerVisible)}
        style={{
          backgroundColor,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.closeButton} onClick={onClose}>
          <CircleCloseButton color="#828282" />
        </div>
        <div className={styles.site}>
          {site?.branding?.onlineOrdering?.sideBarLogo &&
            template.menuBar.site.showSiteIcon && (
              <img
                src={uploadcareConfig.getUrl(
                  site.branding?.onlineOrdering?.sideBarLogo?.fileId,
                  site.branding?.onlineOrdering?.sideBarLogo?.modifiers,
                )}
                alt={site.branding?.onlineOrdering?.sideBarLogo?.fileName}
              />
            )}
          <span>{site?.siteName}</span>
        </div>
        {site && (
          <div
            className={styles.siteDetails}
            style={{ backgroundColor: secondaryBackgroundColor }}
          >
            <div className={styles.siteDetailsRow}>
              <LocationIcon />
              <span>
                {site.siteAddress.address1}
                <br />
                {site.siteAddress.city}
              </span>
            </div>
            {site.openingTimes?.length && (
              <div className={styles.siteDetailsRow}>
                <ClockIcon />
                <span>
                  {normaliseAvailability(
                    getApplicableStoreHours(mapSiteToSiteListObj(site))
                      .availability,
                  )
                    .map(
                      (av) =>
                        `${combineDays(av.applies)} ${timeFromObject(
                          av.startsAt,
                        )} - ${timeFromObject(av.endsAt)}`,
                    )
                    .join("\n")}
                </span>
              </div>
            )}
          </div>
        )}
        <CateringNavItem
          entry={cateringEntry}
          variant="drawer"
          color={template.menuBar.menuItem.fontColour}
        />
        <div className={styles.navigationItems}>
          {(menus?.length ?? 0) > 1 && (
            <>
              <div
                className={styles.menuTitle}
                style={{
                  fontSize: menuItemTitleFontSize,
                  color: menuItemFontColor,
                }}
              >
                Menu
              </div>
              {menus?.map((shortMenu) => (
                <div
                  key={shortMenu.code}
                  className={styles.menuSubitem}
                  style={{
                    fontSize: menuItemFontSize,
                    color: menuItemFontColor,
                    fontWeight: menu?.code === shortMenu.code ? 600 : 400,
                  }}
                  onClick={() => {
                    goToMenu(shortMenu.code);
                  }}
                >
                  <span>{shortMenu.name}</span>
                </div>
              ))}
            </>
          )}
        </div>
        <div className={styles.footer}>
          {isAuthEnabled && (
            <div>
              {authenticatedUser ? (
                <div
                  className={styles.authenticatedUser}
                  style={{
                    color: menuItemFontColor,
                  }}
                >
                  <div
                    className={styles.profileButton}
                    style={{
                      backgroundColor: secondaryBackgroundColor,
                    }}
                    onClick={() => openMyAccountModal()}
                  >
                    {showIcons ? <UserIcon color={iconColor} /> : <span />}
                    <div
                      className={styles.userName}
                      style={{
                        fontSize: menuItemFontSize,
                      }}
                    >
                      {`${authenticatedUser.firstName ?? ""} ${
                        authenticatedUser.lastName ?? ""
                      }`.trim()}
                    </div>
                    {useComoAuth && (
                      <ChevronRight
                        className={styles.chevronIcon}
                        color="#828282"
                      />
                    )}
                    <div className={styles.userEmail}>
                      {authenticatedUser.email}
                    </div>
                  </div>
                  <div
                    className={styles.authButton}
                    style={{
                      fontSize: menuItemFontSize,
                      color: menuItemFontColor,
                    }}
                    onClick={() => {
                      navigate(urlGen("Orders", null));
                      onClose();
                    }}
                  >
                    {showIcons && <HistoryIcon color={iconColor} />}
                    <span>Order History</span>
                  </div>
                  <div
                    className={styles.authButton}
                    style={{
                      fontSize: menuItemFontSize,
                      color: menuItemFontColor,
                    }}
                    onClick={() => {
                      const formData = new FormData();
                      formData.append("orgId", orgId);
                      formData.append("siteId", site?.id ?? "");
                      formData.append("orderId", orderId);
                      if (orderSummary && orderSummary.id) {
                        formData.append(
                          "orderSummary",
                          JSON.stringify(orderSummary),
                        );
                      }
                      fetcher.submit(formData, {
                        method: "POST",
                        action: "/auth/logout",
                      });
                      onClose();
                    }}
                  >
                    {showIcons && <LogoutIcon color={iconColor} />}
                    <span>Logout</span>
                  </div>
                </div>
              ) : (
                <div
                  className={styles.authButton}
                  style={{
                    marginTop: "auto",
                    fontSize: menuItemFontSize,
                    color: menuItemFontColor,
                  }}
                  onClick={() => {
                    openAuthDialog();
                    onClose();
                  }}
                >
                  {showIcons && <UserIcon color={iconColor} />}
                  <span>Login / Signup</span>
                </div>
              )}
            </div>
          )}
          <div className={styles.separator} />
          {template.menuBar.general.showSocials && (
            <div className={styles.socials}>
              {template.branding.socials.map((social, index) => (
                <a
                  href={social.url}
                  target="_blank"
                  rel="noreferrer"
                  key={index}
                >
                  {social.customIcon ? (
                    <img
                      src={uploadcareConfig.getUrl(
                        social.customIcon.fileId,
                        social.customIcon.modifiers,
                      )}
                      alt={social.customIcon.fileName}
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={getSocialIcon(social.icon) as IconProp}
                      color={iconColor}
                      size="lg"
                    />
                  )}
                </a>
              ))}
            </div>
          )}
          <div className={styles.footerLinks}>
            <a
              style={{
                color: menuItemFontColor,
              }}
              href={template.global.links.termsAndConditions}
              target="_blank"
              rel="noreferrer"
            >
              <span>Terms & Conditions</span>
            </a>
            <a
              style={{
                color: menuItemFontColor,
              }}
              href={template.global.links.privacyPolicy}
              target="_blank"
              rel="noreferrer"
            >
              <span>Privacy Policy</span>
            </a>
            {template.menuBar.general.showCopyright && (
              <span className={styles.copyright}>
                {template.branding.copyrightMessage}
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default MenuBarSide;
